/* =========================================================================
 *  FM Options Sheet — mobile "Choose your options" bottom sheet
 *  -----------------------------------------------------------
 *  Owns the entire variant decision: options → quantity → live price →
 *  Add to Basket. Reads the JSON data island emitted by
 *  snippets/fm-options-sheet.liquid; renders one option step per axis in
 *  the product's own option order; recomputes every displayed figure in
 *  the same frame as any selection change.
 *
 *  Contract with the rest of the PDP
 *    - Add to Basket delegates to the legacy globals
 *      `customProductAdded(variantId, qty, properties)` and
 *      `customLengthProductAdded(variantId, lengthCm, properties, qty)`
 *      from snippets/multioptions-script.liquid. They own the /cart/add.js
 *      call, the per-cm quantity convention and the cart-drawer handoff —
 *      reimplementing any of that here would fork the pricing convention.
 *    - Part numbers come from `[data-fm-part-number-map]`
 *      (snippets/pdp-part-number.liquid). Its bracket-stripping rules must
 *      stay in step with whatever generates the searchable `part:` tags, so
 *      the sheet reads the map rather than deriving numbers itself.
 *    - `body.fm-sheet-active` tells the CSS to hide the legacy on-page
 *      option areas on mobile; `body.fm-sheet-open` hides the page footer
 *      and the chat FAB while the sheet is up.
 *
 *  Mobile only — matches the CSS `@media (max-width: 767px)`.
 * ========================================================================= */
(function () {
  'use strict';

  var MOBILE_BREAKPOINT = 767;
  var MIN_CUSTOM_CM = 50;
  /* Catalogue-wide ceiling, used only where the product itself implies no
     shorter one. See `maxCustomCm()` for how roll products narrow it. */
  var DEFAULT_MAX_CM = 2500;

  /* Delivery promises. Stocked sizes ship next working day; custom cuts are
     made to order. Both kept short enough to hold one line at 375px. */
  var NEXT_DAY_NOTE = 'Next working day delivery';
  var CUSTOM_CUT_NOTE = 'Cut to your size — 3 to 5 working days';

  function isMobile() {
    return window.innerWidth <= MOBILE_BREAKPOINT;
  }

  function money(pence) {
    return '£' + (pence / 100).toFixed(2);
  }

  /* Whether the page is in Inc-VAT display mode. fm-vat.js owns the mode
     (the Google Shopping `?incl_vat=true` rule, gclid, the manual toggle)
     and exposes it as window.fmVatMode; the URL check is only a fallback
     for the moment before fm-vat.js has run. */
  function vatInc() {
    if (window.fmVatMode) return window.fmVatMode === 'inc';
    return /[?&]incl_vat=true/i.test(window.location.search);
  }

  /* DISPLAY money — follows the page's VAT mode. Everything the shopper
     reads goes through this; the two CTAs deliberately do not (the sitewide
     rule is that the cart button stays ex VAT even when prices flip). */
  function dispMoney(pence) {
    return money(vatInc() ? Math.round(pence * 1.2) : pence);
  }

  function vatSuffix() {
    return vatInc() ? 'Inc. VAT' : '+ VAT';
  }

  /* Option values are matched on their exact raw strings rather than on a
     handleized form. Liquid's `handleize` and any JS equivalent drift on
     punctuation ("Black / Steel", "60cm x 90cm (2' x 3' Approx)"), and a
     near-miss here silently resolves to the wrong variant. */
  function variantKey(values) {
    return values.join('');
  }

  document.addEventListener('DOMContentLoaded', function () {
    /* The sheet is absent on single-SKU products — the footer is the whole UI
       there — so only the data island and the footer are required. */
    var root = document.querySelector('[data-fm-sheet]');
    var bar = document.querySelector('[data-fm-buybar]');
    var dataNode = document.querySelector('[data-fm-sheet-data]');
    if (!dataNode || !bar) return;

    var DATA;
    try {
      DATA = JSON.parse(dataNode.textContent);
    } catch (e) {
      /* A malformed island means no options could be rendered — leave the
         legacy on-page form as the visible UI rather than showing an empty
         sheet the shopper cannot use. */
      return;
    }
    if (!DATA || !DATA.axes || !DATA.axes.length) return;

    var triggers = document.querySelectorAll('.pdp-options-trigger');

    /* Sheet-scoped query that tolerates the sheet not existing at all — on a
       single-SKU product the footer is the entire buy flow. */
    function sq(sel) {
      return root ? root.querySelector(sel) : null;
    }

    /* Bind only if the element exists. Every sheet control is absent on a
       single-SKU product, so unguarded addEventListener calls would throw
       before the footer ever got wired. */
    function on(el, ev, fn) {
      if (el) el.addEventListener(ev, fn);
    }

    /* ---------------------------------------------------------------- *
     *  Lookups
     * ---------------------------------------------------------------- */
    var variantsByKey = {};
    DATA.variants.forEach(function (v) {
      variantsByKey[variantKey(v.options)] = v;
    });

    /* Axis index → axis, plus the index of the axis that holds sizes. The
       size axis is the one that can carry custom values and the one the CTA
       prompts for by name. */
    var axes = DATA.axes;
    var sizeAxisIndex = -1;
    axes.forEach(function (a, i) {
      if (a.kind === 'size' && sizeAxisIndex === -1) sizeAxisIndex = i;
    });

    /* ---------------------------------------------------------------- *
     *  State
     *  ------
     *  `selected` holds one raw option value per axis, or null. Custom
     *  length adds `customLen` (string, as typed) and `touched` (whether
     *  the shopper has typed into it yet — helper text stays neutral grey
     *  until they have).
     * ---------------------------------------------------------------- */
    var state = {
      selected: axes.map(function () { return null; }),
      /* Pre-filled with the minimum cut (09-Oct-2026) so a shopper who lands
         on a custom-length variant from Shopping sees the 50cm price the
         listing promised, instead of an empty box and no total. `touched`
         stays false so the helper text keeps its neutral styling until they
         type. Must stay in step with MIN_CUSTOM_CM. */
      customLen: String(MIN_CUSTOM_CM),
      touched: false,
      /* Cut-from-standard entry (`DATA.cutFromStandard` products only) — the
         W and L in mm as typed, and whether the group is expanded. Kept as
         strings so a half-typed "12" is distinguishable from empty. */
      cutW: '',
      cutL: '',
      cutOpen: false,
      qty: 1,
      banner: null,
      /* True while an add is in flight. Both CTAs render as "Adding to
         basket…" with a spinner and ignore taps — on a slow connection the
         old instant reset invited spam-clicks and double adds. */
      busy: false
    };

    /* Preselect ONLY the visual axes — colour and texture. They are
       non-blocking by design: a shopper should never be told to "choose a
       texture" when performance is identical across them, and the hero
       image needs something to show on open.

       Every other axis starts unchosen and the CTA prompts for it by name.
       That includes price-bearing choice axes like the spill kits' Kit Type
       (Full Kit £111.57 vs Refill £64.25) — preselecting Full Kit silently
       put the dearer option in the basket path and hid that a refill
       existed at all (Jack, 31-Jul-2026). It also matches the rubber
       prototype, whose initial state is tIdx: null with a "Choose a
       thickness" prompt.

       Two exceptions, both cases where there is nothing to choose:
         - a single-SKU product (no sheet at all — the footer is the
           selected state from first paint, so every axis must resolve)
         - an axis carrying exactly one value

       Runs at init AND after every successful add (see resetSelection),
       so the same rules always describe a "fresh" sheet. */
    function applyPreselects() {
      axes.forEach(function (axis, i) {
        var nothingToChoose = !DATA.hasOptions || axis.values.length === 1;
        var isVisual = axis.kind === 'colour' || axis.kind === 'texture';
        if (!nothingToChoose && !isVisual) {
          state.selected[i] = null;
          return;
        }
        var firstVal = axis.values[0];
        state.selected[i] = firstVal ? firstVal.value : null;
      });
    }
    applyPreselects();

    function selectedAxisValue(i) {
      return state.selected[i];
    }

    function axisValueObj(i, value) {
      var found = null;
      axes[i].values.forEach(function (v) {
        if (v.value === value) found = v;
      });
      return found;
    }

    /* The size axis value currently chosen, as its value object (so callers
       can ask `isCustom` / `widthLabel` without re-looking it up). */
    function selectedSize() {
      if (sizeAxisIndex === -1) return null;
      var val = state.selected[sizeAxisIndex];
      return val ? axisValueObj(sizeAxisIndex, val) : null;
    }

    /* The option values the basket will actually receive. Identical to
       `state.selected` except on a cut-to-size entry, where the matched
       standard sheet IS the size being bought — the cut dimensions travel
       separately as line-item properties. Defined below `cutState`. */
    function selectionValues() {
      var vals = state.selected.slice();
      var cut = cutState();
      if (cut.fit) vals[sizeAxisIndex] = cut.fit.value;
      return vals;
    }

    /* Resolve the variant for the current selection. Returns null while any
       axis is still unset. */
    function currentVariant() {
      var vals = selectionValues();
      if (vals.some(function (v) { return v === null; })) return null;
      return variantsByKey[variantKey(vals)] || null;
    }

    /* ---------------------------------------------------------------- *
     *  Pricing engine
     *  --------------
     *  One function returns every figure the footer needs, so the chips,
     *  the total, the CTA label and the applied-tier strip can never
     *  disagree with each other — they all read the same object.
     * ---------------------------------------------------------------- */
    function parseLen() {
      var n = parseInt(state.customLen, 10);
      return isNaN(n) ? null : n;
    }

    /* Longest cuttable length for the CURRENT selection.
     *
     * On roll products the size values encode the roll the piece is cut from
     * ("140cm Wide x 10m Length"), and nothing can be cut longer than its
     * roll — so the longest roll still available at the chosen thickness IS
     * the custom maximum. That is what makes 8mm+ drop the ceiling from 10 m
     * to 5 m with no thickness rule written anywhere: above 6mm the 10 m roll
     * simply has no variant, so it stops counting.
     *
     * Products whose sizes are finished dimensions ("60cm x 90cm") imply no
     * such ceiling and fall back to the catalogue limit. The `m` unit is what
     * distinguishes the two — "90cm" never matches, "10m" does. */
    function maxCustomCm() {
      if (sizeAxisIndex === -1) return DEFAULT_MAX_CM;
      var longest = null;
      axes[sizeAxisIndex].values.forEach(function (v) {
        if (v.isCustom) return;
        var variant = variantForAxisValue(sizeAxisIndex, v.value);
        if (!variant || variant.available === false) return;
        var m = /(\d+(?:\.\d+)?)\s*m\b/i.exec(v.value);
        if (!m) return;
        var cm = Math.round(parseFloat(m[1]) * 100);
        if (longest === null || cm > longest) longest = cm;
      });
      return longest === null ? DEFAULT_MAX_CM : longest;
    }

    function lenValid() {
      var n = parseLen();
      return n !== null && n >= MIN_CUSTOM_CM && n <= maxCustomCm();
    }

    /* "5 m (500 cm)" / "25 m (2500 cm)" — one phrasing for the helper text
       and the error, so a re-clamp reads as the same fact restated. */
    function maxLabel() {
      var max = maxCustomCm();
      return (max / 100) + ' m (' + max + ' cm)';
    }

    /* ---------------------------------------------------------------- *
     *  Cut from standard
     *  ----------------
     *  The third custom-size shape, and the only one with no variant of its
     *  own: the shopper's exact W × L in mm is cut DOWN from whichever
     *  standard sheet it fits inside, and they pay that sheet's price. So
     *  this does not add an axis value — it RESOLVES one, and every figure
     *  downstream (price, tiers, part number, chips, CTA) then comes from
     *  the ordinary standard-size code path.
     *
     *  Mirrors the desktop panel in `nocustom-multioptions-form-v2.liquid`:
     *  same parse, same cheapest-fit rule, same cart properties. The rules
     *  live in two places because the two surfaces are separate DOM — change
     *  both together.
     * ---------------------------------------------------------------- */
    var CUT_ENABLED = !!DATA.cutFromStandard && sizeAxisIndex !== -1;

    /* Every dimension in mm is an integer here, so `MM` intentionally does
       not accept a decimal — "1220.5" is not a size we cut to. */
    var CUT_MM = /(\d+(?:\.\d+)?)\s*mm\s*[x×]\s*(\d+(?:\.\d+)?)\s*mm/i;

    /* The sheets that can be cut from, for the CURRENT selection of every
       other axis. `min`/`max` normalise orientation, so a 3480 × 1790
       request still fits the 1830 × 3660 mat sideways. Unavailable variants
       are excluded — we cannot cut from a sheet we cannot buy. */
    function cutSheets() {
      var out = [];
      if (!CUT_ENABLED) return out;
      axes[sizeAxisIndex].values.forEach(function (v) {
        if (v.isCustom) return;
        var m = CUT_MM.exec(v.value);
        if (!m) return;
        var variant = variantForAxisValue(sizeAxisIndex, v.value);
        if (!variant || variant.available === false) return;
        var a = parseFloat(m[1]);
        var b = parseFloat(m[2]);
        out.push({
          value: v.value,
          label: v.label,
          min: Math.min(a, b),
          max: Math.max(a, b),
          price: variant.price
        });
      });
      return out;
    }

    /* One object describing the whole entry, so the panel, the footer, the
       CTA and the cart payload cannot disagree about it:
         entered  the shopper has typed something (either field)
         fit      the matched sheet, or null
         error    why there is no fit — the message shown to the shopper
       `entered && !fit` is the BLOCKING state: a part-filled or unmatchable
       size must never fall through to a previously selected standard size,
       which is the same rule the desktop ATC handler enforces. */
    function cutState() {
      var out = { enabled: false, entered: false, w: null, l: null, fit: null, error: null, biggest: null };
      if (!CUT_ENABLED) return out;

      var sheets = cutSheets();
      if (!sheets.length) return out;
      out.enabled = true;
      out.biggest = sheets.reduce(function (best, s) {
        return (s.min * s.max > best.min * best.max) ? s : best;
      });

      if (state.cutW === '' && state.cutL === '') return out;
      out.entered = true;

      var w = parseFloat(state.cutW);
      var l = parseFloat(state.cutL);
      if (!(w > 0) || !(l > 0)) {
        out.error = 'Enter both a width and a length in mm.';
        return out;
      }
      out.w = w;
      out.l = l;

      var reqMin = Math.min(w, l);
      var reqMax = Math.max(w, l);
      sheets.forEach(function (s) {
        if (reqMin <= s.min && reqMax <= s.max && (!out.fit || s.price < out.fit.price)) {
          out.fit = s;
        }
      });
      if (!out.fit) {
        out.error = 'That size is bigger than our largest mat (' + out.biggest.label +
                    '). Please contact us and we can help.';
      }
      return out;
    }

    /* "700 × 500mm" — the size as the shopper asked for it, used on the chip
       and in the panel's confirmation line. */
    function cutSizeWords(cut) {
      return cut.w + ' × ' + cut.l + 'mm';
    }

    function pricing() {
      var variant = currentVariant();
      var size = selectedSize();
      var isCustom = !!(size && size.isCustom);
      var cut = cutState();

      var out = {
        variant: variant,
        isCustom: isCustom,
        /* The cut entry as `cutState` describes it, plus the shorthand the
           renderers actually branch on. Always present (disabled products
           get `enabled: false`) so no caller has to null-check it. */
        cut: cut,
        isCut: !!cut.fit,
        /* `unitBase` is the undiscounted per-unit price in pence; `unit` is
           what the shopper actually pays each after tiers. */
        unitBase: null,
        unit: null,
        totalEx: 0,
        tiers: [],
        activeTier: null,
        nextTier: null,
        ready: false
      };

      /* A part-filled or unmatchable cut size blocks the whole sheet. Without
         this the shopper types 4000 × 4000, gets an error in the panel, and
         the footer still offers to sell them whatever standard size happened
         to be selected before — at its price. */
      if (cut.entered && !cut.fit) return out;

      if (!variant) return out;

      if (isCustom) {
        /* Custom cut: the variant price IS the per-cm rate, so the unit
           price is rate × length. No tiers by business rule, so the tier
           block below is skipped entirely. */
        if (!lenValid()) return out;
        out.unitBase = Math.round(variant.price * parseLen());
        out.unit = out.unitBase;
        out.totalEx = out.unit * state.qty;
        out.ready = true;
        return out;
      }

      out.unitBase = variant.price;
      out.tiers = variant.tiers || [];

      /* Highest qualifying tier wins; `nextTier` is the cheapest threshold
         still above the current quantity. Tiers arrive sorted ascending. */
      out.tiers.forEach(function (t) {
        if (state.qty >= t[0]) out.activeTier = t;
      });
      for (var i = 0; i < out.tiers.length; i++) {
        if (state.qty < out.tiers[i][0]) { out.nextTier = out.tiers[i]; break; }
      }

      out.unit = out.activeTier ? tierUnit(out.unitBase, out.activeTier) : out.unitBase;
      out.totalEx = out.unit * state.qty;
      out.ready = true;
      return out;
    }

    /* Per-unit price a given tier charges — used for the "4+ from £17.49
     * each" row hints, the savings maths, and the total on the CTA.
     *
     * The rounding here is not cosmetic. Whatever applies the store's bulk
     * discount takes an INTEGER-PENCE discount per unit — floor(base × pct)
     * — so a 3% break on £29.52 charges £28.64, not the £28.63 that
     * rounding the discounted price to nearest would give. Verified against
     * /cart.js at the 3 / 5 / 8 / 20% tiers on 31-Jul-2026: nearest-rounding
     * was 1p per unit low at 3% and 5% and agreed at 8% and 20%, which is
     * exactly the signature of a truncated discount amount.
     *
     * Getting this wrong puts a price on the CTA that the cart then
     * contradicts, which is the one thing the pinned total exists to prevent.
     */
    function tierUnit(basePence, tier) {
      return basePence - Math.floor(basePence * tier[1] / 100);
    }

    /* Does ANY variant exist for this axis value, holding every OTHER
       non-size axis at its current selection? AtEase has no Ribbed
       Black/Yellow variant at any size — its Ribbed card must grey out
       while Black/Yellow is the colour, rather than lead to an empty size
       list. The size axis is deliberately ignored: a texture that exists
       in three of six sizes is still a real choice, and the size step
       handles per-size gaps itself. */
    function valueHasVariant(axisIndex, value) {
      return DATA.variants.some(function (v) {
        if (v.options[axisIndex] !== value) return false;
        for (var j = 0; j < axes.length; j++) {
          if (j === axisIndex || j === sizeAxisIndex) continue;
          if (state.selected[j] !== null && v.options[j] !== state.selected[j]) return false;
        }
        return true;
      });
    }

    /* The variant a given axis value would resolve to, holding every OTHER
       axis at its current selection. This is what makes rows reprice live
       when a price-bearing axis changes (colour on AtEase, thickness on
       rubber) instead of showing a stale price from page load. */
    function variantForAxisValue(axisIndex, value) {
      var probe = state.selected.slice();
      probe[axisIndex] = value;
      if (probe.some(function (v) { return v === null; })) return null;
      return variantsByKey[variantKey(probe)] || null;
    }

    /* ---------------------------------------------------------------- *
     *  Part number — read from pdp-part-number.liquid's variant map
     * ---------------------------------------------------------------- */
    var partMap = null;
    (function () {
      var node = document.querySelector('[data-fm-part-number-map]');
      if (!node) return;
      try { partMap = JSON.parse(node.textContent); } catch (e) { partMap = null; }
    })();

    /* Deliberately NOT length-aware for custom cuts. A custom variant's part
       number is the same whatever length is ordered, and it has to stay that
       way: the same numbers are what generate the searchable `part:` tags, so
       appending "/250" would mint a part number no search could ever match.
       The cut length travels as the `Length (cm)` line-item property instead,
       which is also how the legacy ATC records it. */
    function partNumberFor(variant) {
      if (!partMap || !partMap.base || !variant) return '';
      var suffix = (partMap.variants && partMap.variants[variant.id]) || '';
      return suffix ? partMap.base + '/' + suffix : partMap.base;
    }

    /* ================================================================ *
     *  Rendering — option body
     * ================================================================ */
    var body = sq('[data-fm-sheet-body]');

    function el(tag, cls, text) {
      var node = document.createElement(tag);
      if (cls) node.className = cls;
      if (text !== undefined && text !== null) node.textContent = text;
      return node;
    }

    function stepHeader(axis, valueText, hint) {
      var head = el('div', 'fm-sheet__step-head');
      head.appendChild(el('span', 'fm-sheet__step-label', axis.name));
      if (valueText) head.appendChild(el('span', 'fm-sheet__step-value', valueText));
      if (hint) head.appendChild(el('span', 'fm-sheet__step-hint', hint));
      return head;
    }

    /* ---- SwatchRow (colour) ---- */
    function renderSwatchRow(axis, axisIndex) {
      var wrap = el('div', 'fm-sheet__step');
      var current = selectedAxisValue(axisIndex);
      wrap.appendChild(stepHeader(axis, current || ''));

      var row = el('div', 'fm-sheet__swatches');
      axis.values.forEach(function (v) {
        var btn = el('button', 'fm-sheet__swatch');
        btn.type = 'button';
        btn.setAttribute('aria-label', v.value);
        btn.setAttribute('aria-pressed', String(v.value === current));
        if (v.value === current) btn.classList.add('is-selected');
        if (!valueHasVariant(axisIndex, v.value)) {
          btn.classList.add('is-unavailable');
          btn.disabled = true;
        }
        var img = document.createElement('img');
        img.src = v.swatch || '';
        img.alt = '';
        img.loading = 'lazy';
        btn.appendChild(img);
        btn.addEventListener('click', function () {
          pickAxis(axisIndex, v.value);
        });
        row.appendChild(btn);
      });
      wrap.appendChild(row);
      return wrap;
    }

    /* ---- PhotoCardRow (surface texture) ---- */
    function renderPhotoCards(axis, axisIndex) {
      var wrap = el('div', 'fm-sheet__step');
      var current = selectedAxisValue(axisIndex);
      /* "Performance is identical" stops shoppers stalling on a choice that
         does not affect price or function — it is the reassurance the handoff
         calls for, not decoration. */
      wrap.appendChild(stepHeader(axis, current || '', 'Performance is identical'));

      var row = el('div', 'fm-sheet__cards');
      axis.values.forEach(function (v) {
        var btn = el('button', 'fm-sheet__card');
        btn.type = 'button';
        btn.setAttribute('aria-pressed', String(v.value === current));
        if (v.value === current) btn.classList.add('is-selected');
        if (!valueHasVariant(axisIndex, v.value)) {
          btn.classList.add('is-unavailable');
          btn.disabled = true;
        }

        var thumb = el('span', 'fm-sheet__card-thumb');
        /* Prefer the variant image for THIS colour × texture combination so
           a yellow-border colour previews with its border. Falls back to the
           axis value's own image when that combination has no image. */
        var combo = variantForAxisValue(axisIndex, v.value);
        var src = (combo && combo.image) || v.image || v.swatch;
        if (src) {
          var img = document.createElement('img');
          img.src = src;
          img.alt = '';
          img.loading = 'lazy';
          thumb.appendChild(img);
        }
        btn.appendChild(thumb);
        btn.appendChild(el('span', 'fm-sheet__card-label', v.label));
        btn.addEventListener('click', function () {
          pickAxis(axisIndex, v.value);
        });
        row.appendChild(btn);
      });
      wrap.appendChild(row);
      return wrap;
    }

    /* ---- ChipGrid (thickness) ---- */
    function renderChipGrid(axis, axisIndex) {
      var wrap = el('div', 'fm-sheet__step');
      var current = selectedAxisValue(axisIndex);
      wrap.appendChild(stepHeader(axis, current || ''));

      var grid = el('div', 'fm-sheet__chip-grid');
      axis.values.forEach(function (v) {
        var btn = el('button', 'fm-sheet__grid-chip');
        btn.type = 'button';
        btn.setAttribute('aria-pressed', String(v.value === current));
        if (v.value === current) btn.classList.add('is-selected');
        if (!valueHasVariant(axisIndex, v.value)) {
          btn.classList.add('is-unavailable');
          btn.disabled = true;
        }
        btn.appendChild(el('span', 'fm-sheet__grid-chip-label', v.label));

        /* A "from" price only helps if it is the cheapest thing you can buy
           at that thickness — so it scans every size for the lowest price
           rather than reading one arbitrary variant. */
        var from = null;
        if (sizeAxisIndex !== -1) {
          axes[sizeAxisIndex].values.forEach(function (sv) {
            var probe = state.selected.slice();
            probe[axisIndex] = v.value;
            probe[sizeAxisIndex] = sv.value;
            if (probe.some(function (x) { return x === null; })) return;
            var cand = variantsByKey[variantKey(probe)];
            if (cand && cand.available && !cand.isCustom) {
              if (from === null || cand.price < from) from = cand.price;
            }
          });
        }
        if (from !== null) {
          btn.appendChild(el('span', 'fm-sheet__grid-chip-price', 'from ' + dispMoney(from)));
        }
        btn.addEventListener('click', function () {
          pickAxis(axisIndex, v.value);
        });
        grid.appendChild(btn);
      });
      wrap.appendChild(grid);
      return wrap;
    }

    /* ---- OptionRow list (sizes / lengths / backing) + CustomLengthGroup ---- */
    function renderSizeStep(axis, axisIndex) {
      var wrap = el('div', 'fm-sheet__step');
      wrap.appendChild(stepHeader(axis, ''));

      /* If a prior blocking axis is still unchosen, none of these rows can
         resolve a variant — every price would be blank and every row dead.
         Render the prototype's dashed placeholder naming what to pick
         instead (rubber 3a: "Choose a thickness above to see lengths and
         prices"), and skip the rows AND the custom group entirely. */
      var missingAxis = -1;
      state.selected.forEach(function (v, j) {
        if (v === null && j !== axisIndex && missingAxis === -1) missingAxis = j;
      });
      if (missingAxis !== -1) {
        wrap.appendChild(el(
          'div',
          'fm-sheet__step-placeholder',
          'Choose a ' + axes[missingAxis].name.toLowerCase() +
          ' above to see ' + (axis.kind === 'size' ? 'sizes' : 'options') +
          ' and prices'
        ));
        return wrap;
      }

      var current = selectedAxisValue(axisIndex);

      axis.values.forEach(function (v) {
        if (v.isCustom) return; /* custom rows render last, below */

        var variant = variantForAxisValue(axisIndex, v.value);

        /* No variant for this combination → the value does not exist at the
           current selection, so it must not render at all. This is the whole
           dependent-constraint mechanism: the rubber sheet's 10 m roll stops
           being offered above 6mm because no 10 m variant exists there, and
           the 5 m roll appears in its place. Rendering a priceless, dead row
           instead would be exactly the kind of dead end the redesign removes. */
        if (!variant) return;

        var row = el('button', 'fm-sheet__row');
        row.type = 'button';
        row.setAttribute('aria-pressed', String(v.value === current));
        if (v.value === current) row.classList.add('is-selected');
        if (variant && variant.available === false) {
          row.classList.add('is-unavailable');
          row.disabled = true;
        }

        var left = el('span', 'fm-sheet__row-left');
        left.appendChild(el('span', 'fm-sheet__row-label', v.label));

        /* No inline tier hint — bulk-break advertising left the sheet on
           31-Jul-2026 (too busy; the cart drawer does the upselling). The
           PRICING still honours tiers so the CTA matches checkout. */
        if (variant && variant.available === false) {
          left.appendChild(el('span', 'fm-sheet__row-sub', 'Out of stock'));
        }
        row.appendChild(left);

        var right = el('span', 'fm-sheet__row-right');
        if (variant) {
          right.appendChild(el('span', 'fm-sheet__row-price', dispMoney(variant.price)));
          right.appendChild(el('span', 'fm-sheet__row-vat', vatSuffix()));
        }
        row.appendChild(right);

        row.addEventListener('click', function () {
          pickAxis(axisIndex, v.value);
        });
        wrap.appendChild(row);
      });

      /* ---- Custom length ----
         One custom option, not two rows plus an accordion. Each custom value
         is a width, so multiple widths become a segmented control inside a
         single group rather than separate size rows. */
      /* Only offer widths that actually resolve to a purchasable custom
         variant for the CURRENT colour/texture — AtEase sells no custom cut
         in some combinations, and an orderable-looking group with no variant
         behind it is a dead end at the CTA. */
      var customValues = axis.values.filter(function (v) {
        if (!v.isCustom) return false;
        var cv = variantForAxisValue(axisIndex, v.value);
        return !!cv && cv.available !== false;
      });
      if (customValues.length) {
        var isCustomSelected = !!(current && axisValueObj(axisIndex, current) &&
                                  axisValueObj(axisIndex, current).isCustom);

        var group = el('div', 'fm-sheet__custom');
        if (isCustomSelected) group.classList.add('is-selected');

        var head = el('button', 'fm-sheet__row fm-sheet__row--custom');
        head.type = 'button';
        head.setAttribute('aria-expanded', String(isCustomSelected));
        var hLeft = el('span', 'fm-sheet__row-left');
        hLeft.appendChild(el('span', 'fm-sheet__row-label', 'Custom length'));
        var widthWords = customValues.map(function (v) {
          return (v.widthLabel || '').replace(/\s*wide\s*$/i, '');
        }).filter(Boolean).join(' or ');
        hLeft.appendChild(el(
          'span',
          'fm-sheet__row-custom-sub',
          widthWords ? 'Cut to your size · ' + widthWords + ' wide' : 'Cut to your size'
        ));
        head.appendChild(hLeft);

        var hRight = el('span', 'fm-sheet__row-right');
        var p = pricing();
        if (isCustomSelected && p.ready) {
          /* A valid custom price renders in standard ink with a plain
             VAT caption — never red, never alongside an error. */
          hRight.appendChild(el('span', 'fm-sheet__row-price', dispMoney(p.unitBase)));
          hRight.appendChild(el('span', 'fm-sheet__row-vat', vatSuffix()));
        } else {
          var cheapestRate = null;
          customValues.forEach(function (v) {
            var cv = variantForAxisValue(axisIndex, v.value);
            if (cv && (cheapestRate === null || cv.price < cheapestRate)) cheapestRate = cv.price;
          });
          if (cheapestRate !== null) {
            var rateDisp = vatInc() ? Math.round(cheapestRate * 1.2) : cheapestRate;
            hRight.appendChild(el(
              'span',
              'fm-sheet__row-vat',
              'from £' + (rateDisp / 100).toFixed(2) + '/cm'
            ));
          }
        }
        head.appendChild(hRight);
        head.addEventListener('click', function () {
          /* Opening Custom length IS a selection — it takes over the size
             axis rather than sitting alongside a still-selected standard
             size. Picking a standard row switches straight back. */
          var target = current && axisValueObj(axisIndex, current) &&
                       axisValueObj(axisIndex, current).isCustom
            ? current
            : customValues[0].value;
          pickAxis(axisIndex, target);
        });
        group.appendChild(head);

        if (isCustomSelected) {
          var panel = el('div', 'fm-sheet__custom-panel');

          if (customValues.length > 1) {
            var seg = el('div', 'fm-sheet__seg');
            customValues.forEach(function (v) {
              var b = el('button', 'fm-sheet__seg-btn', v.widthLabel || v.label);
              b.type = 'button';
              b.setAttribute('aria-pressed', String(v.value === current));
              if (v.value === current) b.classList.add('is-selected');
              b.addEventListener('click', function () {
                pickAxis(axisIndex, v.value);
              });
              seg.appendChild(b);
            });
            panel.appendChild(seg);
          }

          var field = el('div', 'fm-sheet__field');
          var input = document.createElement('input');
          input.type = 'text';
          input.inputMode = 'numeric';
          input.className = 'fm-sheet__input';
          input.value = state.customLen;
          input.placeholder = 'Length';
          input.setAttribute('aria-label', 'Length in centimetres');
          input.setAttribute('data-fm-sheet-len', '');
          input.addEventListener('input', function () {
            var clean = input.value.replace(/[^0-9]/g, '');
            state.customLen = clean;
            state.touched = true;
            state.banner = null;
            /* Repaint the footer live, but do NOT re-render the body — that
               would replace the input mid-type and drop focus and caret. */
            input.value = clean;
            renderFooter();
            updateCustomInlinePrice();
            updateHelper();
          });
          field.appendChild(input);
          field.appendChild(el('span', 'fm-sheet__field-suffix', 'cm long'));
          panel.appendChild(field);

          panel.appendChild(el('span', 'fm-sheet__helper', ''));
          group.appendChild(panel);
        }

        wrap.appendChild(group);
      }

      /* ---- Cut to size ----
         Rendered last, below the standard sizes, in the same collapsed-row
         shape as Custom length so the two read as one family. */
      if (axisIndex === sizeAxisIndex) {
        var cutGroup = renderCutGroup();
        if (cutGroup) wrap.appendChild(cutGroup);
      }

      return wrap;
    }

    /* ---- CutToSizeGroup ----
       Unlike Custom length this is NOT an axis value, so tapping the head row
       only expands the panel — the size is chosen by what gets typed. The row
       stays visually selected while an entry is live so the shopper can see
       which of the two routes their price is coming from. */
    function renderCutGroup() {
      var cut = cutState();
      if (!cut.enabled) return null;

      /* An entry keeps the group open across re-renders (a colour change, a
         VAT flip) — collapsing it would hide the fields that are producing
         the current price. */
      var open = state.cutOpen || cut.entered;

      var group = el('div', 'fm-sheet__custom fm-sheet__cut');
      if (cut.fit) group.classList.add('is-selected');

      var head = el('button', 'fm-sheet__row fm-sheet__row--custom');
      head.type = 'button';
      head.setAttribute('aria-expanded', String(open));

      var hLeft = el('span', 'fm-sheet__row-left');
      hLeft.appendChild(el('span', 'fm-sheet__row-label', 'Enter a custom size'));
      hLeft.appendChild(el(
        'span',
        'fm-sheet__row-custom-sub',
        'Cut from the nearest standard mat · you keep the offcuts'
      ));
      head.appendChild(hLeft);

      var hRight = el('span', 'fm-sheet__row-right');
      if (cut.fit) {
        hRight.appendChild(el('span', 'fm-sheet__row-price', dispMoney(cut.fit.price)));
        hRight.appendChild(el('span', 'fm-sheet__row-vat', vatSuffix()));
      }
      head.appendChild(hRight);

      head.addEventListener('click', function () {
        state.cutOpen = !open;
        state.banner = null;
        renderBody();
        renderFooter();
        if (state.cutOpen) focusCutField('w', true);
      });
      group.appendChild(head);

      if (open) {
        var panel = el('div', 'fm-sheet__custom-panel');

        var fields = el('div', 'fm-sheet__cut-fields');
        fields.appendChild(cutField('w', 'Width (mm)', state.cutW));
        fields.appendChild(cutField('l', 'Length (mm)', state.cutL));
        panel.appendChild(fields);

        /* A <span>, not a <p> — `.section p` in styles-min.scss.liquid is
           (0,1,1) and would beat this component's single class, indenting the
           line and floating it left. Same reason the length helper is one. */
        panel.appendChild(el('span', 'fm-sheet__cut-msg', ''));
        group.appendChild(panel);
      }

      return group;
    }

    function cutField(which, labelText, value) {
      var field = el('label', 'fm-sheet__cut-field');
      field.appendChild(el('span', 'fm-sheet__cut-field-label', labelText));

      var input = document.createElement('input');
      /* `type="text"` with a numeric keypad, matching the length field: a
         real number input brings the spinner, accepts "1e3", and changes
         value on a stray wheel/scroll gesture. */
      input.type = 'text';
      input.inputMode = 'numeric';
      input.className = 'fm-sheet__input';
      input.value = value;
      input.setAttribute('aria-label', which === 'w' ? 'Width in millimetres' : 'Length in millimetres');
      input.setAttribute('data-fm-sheet-cut', which);

      input.addEventListener('input', function () {
        var clean = input.value.replace(/[^0-9]/g, '');
        input.value = clean;
        if (which === 'w') state.cutW = clean; else state.cutL = clean;
        state.banner = null;
        /* Footer only — re-rendering the body would replace the field being
           typed into and drop focus and caret (same rule as the length
           input). Everything else the entry changes is patched in place. */
        renderFooter();
        updateCutPanel();
      });

      field.appendChild(input);
      return field;
    }

    /* Patch the parts of the cut group that change as the shopper types,
       without re-rendering the body under their caret: the head row's
       resolved price, the group's selected state, and the message line. */
    function updateCutPanel() {
      var group = sq('.fm-sheet__cut');
      if (!group) return;
      var cut = cutState();

      group.classList.toggle('is-selected', !!cut.fit);

      var right = group.querySelector('.fm-sheet__row-right');
      if (right) {
        right.textContent = '';
        if (cut.fit) {
          right.appendChild(el('span', 'fm-sheet__row-price', dispMoney(cut.fit.price)));
          right.appendChild(el('span', 'fm-sheet__row-vat', vatSuffix()));
        }
      }

      var msg = group.querySelector('.fm-sheet__cut-msg');
      if (!msg) return;
      msg.classList.toggle('is-error', !!cut.error);
      if (cut.error) {
        msg.textContent = cut.error;
      } else if (cut.fit) {
        /* Naming the sheet it comes from is the reassurance that the price is
           not a mistake — it is a standard mat's price, cut down. */
        msg.textContent = 'Cut from the ' + cut.fit.label + ' mat at ' +
                          dispMoney(cut.fit.price) + ' ' + vatSuffix() +
                          '. You keep the offcuts.';
      } else {
        msg.textContent = 'Any size up to ' + cut.biggest.label + '.';
      }
    }

    function focusCutField(which, select) {
      var input = sq('[data-fm-sheet-cut="' + which + '"]');
      if (!input) return;
      input.focus();
      if (select && input.select) input.select();
    }

    /* Helper text is neutral grey stating the valid range, and only turns red
       once the shopper has typed something out of range. It never appears in
       red next to a valid price. */
    function updateHelper() {
      var helper = sq('.fm-sheet__helper');
      if (!helper) return;
      var n = parseLen();
      var invalid = state.touched && state.customLen !== '' && !lenValid();
      if (invalid) {
        helper.textContent = (n !== null && n > maxCustomCm())
          ? 'Maximum length is ' + maxLabel()
          : 'Minimum length is ' + MIN_CUSTOM_CM + ' cm';
        helper.classList.add('is-error');
      } else {
        helper.textContent = 'Any length from ' + MIN_CUSTOM_CM +
                             ' cm up to ' + maxLabel();
        helper.classList.remove('is-error');
      }
    }

    /* Live price inside the custom row, so the shopper sees the effect of a
       length without scrolling to the footer. */
    function updateCustomInlinePrice() {
      var right = sq('.fm-sheet__row--custom .fm-sheet__row-right');
      if (!right) return;
      var p = pricing();
      right.textContent = '';
      if (p.ready && p.isCustom) {
        right.appendChild(el('span', 'fm-sheet__row-price', dispMoney(p.unitBase)));
        right.appendChild(el('span', 'fm-sheet__row-vat', vatSuffix()));
      }
    }

    /* Axis indices in display order: every non-size axis first (keeping the
       product's own order), the size axis last. Size is the blocking,
       price-bearing decision, so it sits closest to the footer that prices
       it — and non-blocking steps like AtEase's texture cards are no longer
       buried below six size rows where nobody scrolled to find them
       (Jack, 31-Jul-2026). The steps are the same; only the order changes. */
    function renderOrder() {
      var order = [];
      axes.forEach(function (axis, i) {
        if (i !== sizeAxisIndex) order.push(i);
      });
      if (sizeAxisIndex !== -1) order.push(sizeAxisIndex);
      return order;
    }

    function renderBody() {
      if (!body) return;
      body.textContent = '';
      renderOrder().forEach(function (i) {
        var axis = axes[i];
        var step;
        if (axis.kind === 'colour') step = renderSwatchRow(axis, i);
        else if (axis.kind === 'texture') step = renderPhotoCards(axis, i);
        else if (axis.kind === 'thickness') step = renderChipGrid(axis, i);
        else step = renderSizeStep(axis, i); /* size, backing etc. — OptionRow */
        body.appendChild(step);
      });
      updateHelper();
      updateCutPanel();
    }

    /* ================================================================ *
     *  Rendering — pinned footer
     * ================================================================ */
    var F = {
      banner: sq('[data-fm-sheet-banner]'),
      summary: sq('[data-fm-sheet-summary]'),
      chips: sq('[data-fm-sheet-chips]'),
      qtyLine: sq('[data-fm-sheet-qty-line]'),
      partno: sq('[data-fm-sheet-partno]'),
      qty: sq('[data-fm-sheet-qty]'),
      dec: sq('[data-fm-sheet-dec]'),
      inc: sq('[data-fm-sheet-inc]'),
      applied: sq('[data-fm-sheet-applied]'),
      appliedText: sq('[data-fm-sheet-applied-text]'),
      totalEx: sq('[data-fm-sheet-total-ex]'),
      totalVat: sq('[data-fm-sheet-total-vat]'),
      cta: sq('[data-fm-sheet-cta]'),
      delivery: sq('[data-fm-sheet-delivery]')
    };

    /* Delivery promise for the current selection.
     *
     * The distinction that matters is stocked vs custom-cut, and the store
     * already records it per variant in `custom.variant_delivery_time`
     * ("Next Day" on standard sizes, "3 to 5 working days" on custom cuts) —
     * so that metafield is the authority and the strings below are only
     * fallbacks for variants where it is unset.
     *
     * "Next Day" is normalised to "next working day". The mats do not ship on
     * a Saturday, so a bare "next day" on a Friday order is a promise the
     * store does not keep (Jack, 31-Jul-2026). */
    function deliveryText(p) {
      var raw = p.variant && p.variant.deliveryNote;
      if (raw) {
        var note = sentenceCase(String(raw).trim());
        /* A next-day promise. Rendered as "next WORKING day" — the mats do not
           ship on a Saturday, so a bare "next day" on a Friday order is a
           promise the store does not keep. */
        if (/^next\s+(working\s+)?day$/i.test(note)) return NEXT_DAY_NOTE;
        /* A lead time ("3 to 5 working days") is already a complete phrase, so
           it is used verbatim — this is where genuine per-variant differences
           live and must not be flattened. */
        if (/^\d+\s*(?:(?:to|-|–|—)\s*\d+\s*)?(?:working\s+)?(?:day|days|week|weeks)$/i.test(note)) {
          return note;
        }
        /* Anything else is free text the theme cannot vouch for. The
           catalogue contains at least one typo — variant
           "Black / Blue / 90cm x 120cm" on Plush Choice reads "Net Day" —
           and printing that at a customer is worse than falling back to the
           rule below. Reported to Jack 31-Jul-2026 to fix in admin. */
      }
      return p.isCustom ? CUSTOM_CUT_NOTE : NEXT_DAY_NOTE;
    }

    /* The delivery metafield is entered Title Case on some variants ("Next
       Day") and as a normal phrase on others ("3 to 5 working days"), so it
       lands next to the sentence-case fallbacks reading "Next Day delivery".
       House style is sentence case for customer-facing copy, so a Title Cased
       value gets converted.

       Only fires when EVERY alphabetic word starts uppercase — that is what
       distinguishes a title-cased label from a phrase containing a proper
       noun or an initialism. "EU delivery only" and "3 to 5 working days"
       both fail the test and pass through untouched. */
    function sentenceCase(str) {
      var words = str.split(/\s+/).filter(function (w) { return /[a-z]/i.test(w); });
      if (words.length < 2) return str;
      var allTitle = words.every(function (w) {
        return /^[A-Z][a-z]*$/.test(w.replace(/[^A-Za-z]/g, ''));
      });
      if (!allTitle) return str;
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    }

    /* `swatch` is a value object for colour axes, null otherwise. The dot
       prefers the flat CSS from the shared `color-code` map — a 12px circle
       cannot render a product photo legibly, and a solid colour costs no
       request. Only colours missing from that map fall back to the image. */
    function chip(text, swatch, modifier) {
      var c = el('span', 'fm-sheet__chip' + (modifier ? ' ' + modifier : ''));
      if (swatch) {
        var dot = el('span', 'fm-sheet__chip-dot');
        dot.setAttribute('aria-hidden', 'true');
        if (swatch.swatchCss) {
          dot.style.cssText = swatch.swatchCss;
        } else if (swatch.swatch) {
          dot.style.backgroundImage = 'url("' + swatch.swatch + '")';
        }
        c.appendChild(dot);
      }
      c.appendChild(el('span', 'fm-sheet__chip-text', text));
      return c;
    }

    function selectionLabel() {
      /* A cut entry names the sheet it is cut from, so the summary states
         what is actually being bought. The requested size rides alongside on
         its own chip. */
      var cut = cutState();
      if (cut.fit) return cut.fit.label;

      var size = selectedSize();
      if (!size) return null;
      if (size.isCustom) {
        if (!lenValid()) return null;
        /* Width arrives as "90cm wide"; drop the word so the chip reads
           "90cm × 250cm" — one unit convention, matching how the standard
           size values are written in the catalogue ("90cm x 120cm"). */
        var w = (size.widthLabel || '').replace(/\s*wide\s*$/i, '').trim();
        return w ? w + ' × ' + parseLen() + 'cm' : parseLen() + 'cm';
      }
      return size.label;
    }

    function renderSheetFooter(p) {
      if (!root) return;

      /* ---- prompt banner ---- */
      if (state.banner) {
        F.banner.textContent = state.banner;
        F.banner.hidden = false;
      } else {
        F.banner.hidden = true;
        F.banner.textContent = '';
      }

      /* ---- summary block ---- */
      F.summary.hidden = !p.ready;

      if (p.ready) {
        /* chips — one per axis, in option order, so the summary reads the
           same way the body does */
        F.chips.textContent = '';
        axes.forEach(function (axis, i) {
          if (i === sizeAxisIndex) return;
          var v = axisValueObj(i, state.selected[i]);
          if (!v) return;
          F.chips.appendChild(chip(v.value, axis.kind === 'colour' ? v : null));
        });
        var selLabel = selectionLabel();
        if (selLabel) F.chips.appendChild(chip(selLabel));
        if (p.isCustom) {
          F.chips.appendChild(chip('Cut to your size', null, 'fm-sheet__chip--custom'));
        }
        if (p.isCut) {
          F.chips.appendChild(chip('Cut to ' + cutSizeWords(p.cut), null, 'fm-sheet__chip--custom'));
        }

        F.qtyLine.textContent = state.qty + ' × ' + dispMoney(p.unit) + ' each';
        var partNo = partNumberFor(p.variant);
        F.partno.textContent = partNo ? 'Part No. ' + partNo : '';
        F.qty.textContent = String(state.qty);
        F.dec.disabled = state.qty <= 1;

        /* ---- bulk tier: confirmation only ----
           Shown when quantity has actually reached a tier, never before.
           The dashed "add N more" nudge, the per-row tier hints and the
           next-tier upsell were removed on 31-Jul-2026 (too busy — the cart
           drawer does the upselling); this strip is the reward for getting
           there, so the shopper isn't left wondering why the unit price
           dropped. Figures go through dispMoney so they follow the page's
           VAT mode like everything else the shopper reads. */
        if (p.activeTier) {
          var aUnit = tierUnit(p.unitBase, p.activeTier);
          var saved = (p.unitBase - aUnit) * state.qty;
          F.appliedText.textContent = 'Bulk price applied — ' + dispMoney(aUnit) +
                                      ' each (saving ' + dispMoney(saved) + ')';
          F.applied.hidden = false;
        } else {
          F.applied.hidden = true;
        }

        /* One Total line in the page's VAT mode. The CTA stays ex VAT
           (sitewide rule), so under incl_vat the two can differ — same as
           the legacy PDP, where the headline flips and the ATC does not. */
        F.totalEx.textContent = dispMoney(p.totalEx);
        if (F.totalVat) F.totalVat.textContent = vatInc() ? '(inc. VAT)' : '(ex. VAT)';
      }

      /* ---- CTA: never a dead tap ---- */
      renderCta(p);

      /* ---- delivery microcopy ----
         Product- and selection-aware. The variant's own
         `custom.variant_delivery_time` is the authority when it exists — it
         already distinguishes stocked sizes ("Next Day") from custom cuts
         ("3 to 5 working days"), so prefer it over any wording invented
         here. The fallbacks only cover variants with no metafield set. */
      F.delivery.textContent = deliveryText(p);
    }

    /* ================================================================ *
     *  Rendering — closed-state page footer
     * ================================================================ */
    var B = {
      chips: bar.querySelector('[data-fm-buybar-chips]'),
      qtyLine: bar.querySelector('[data-fm-buybar-qty-line]'),
      qty: bar.querySelector('[data-fm-buybar-qty]'),
      dec: bar.querySelector('[data-fm-buybar-dec]'),
      inc: bar.querySelector('[data-fm-buybar-inc]'),
      cta: bar.querySelector('[data-fm-buybar-cta]')
    };

    function renderBar(p) {
      /* Mode drives which regions the CSS reveals. `single` never changes —
         a single-SKU product has nothing to select, so the footer is the
         selected state from first paint. */
      var mode = !DATA.hasOptions ? 'single' : (p.ready ? 'selected' : 'slim');
      bar.setAttribute('data-mode', mode);

      if (mode === 'slim') {
        /* Nothing else to fill: the trigger's label and from-price are
           server-rendered, and a stepper would be meaningless without a
           variant. */
        return;
      }

      B.chips.textContent = '';
      if (mode === 'selected') {
        /* Same chips, in the same order, as the sheet and the cart card —
           that repetition is what makes the three read as one system. */
        axes.forEach(function (axis, i) {
          if (i === sizeAxisIndex) return;
          var v = axisValueObj(i, state.selected[i]);
          if (!v) return;
          B.chips.appendChild(chip(v.value, axis.kind === 'colour' ? v : null));
        });
        var selLabel = selectionLabel();
        if (selLabel) B.chips.appendChild(chip(selLabel));
        if (p.isCustom) {
          B.chips.appendChild(chip('Cut to your size', null, 'fm-sheet__chip--custom'));
        }
        if (p.isCut) {
          B.chips.appendChild(chip('Cut to ' + cutSizeWords(p.cut), null, 'fm-sheet__chip--custom'));
        }
      }

      if (B.qtyLine) {
        B.qtyLine.textContent = p.ready
          ? state.qty + ' × ' + dispMoney(p.unit) + ' each'
          : '';
      }
      B.qty.textContent = String(state.qty);
      B.dec.disabled = state.qty <= 1;

      if (state.busy) {
        B.cta.textContent = 'Adding to basket…';
        B.cta.classList.remove('is-prompt');
        B.cta.classList.add('is-adding');
        B.cta.disabled = true;
      } else if (p.ready) {
        /* CTA price is the ex-VAT total (sitewide rule — the cart button
           never flips with incl_vat). */
        B.cta.textContent = 'Add to Basket — ' + money(p.totalEx);
        B.cta.classList.remove('is-prompt');
        B.cta.classList.remove('is-adding');
        B.cta.disabled = false;
      } else {
        /* Single-SKU with an unavailable variant is the only way to land here
           in `single` mode. Say so rather than offering a priced CTA. */
        B.cta.textContent = 'Out of stock';
        B.cta.classList.add('is-prompt');
      }
    }

    /* Single entry point — every interaction calls this, so the sheet and the
       page footer can never show different figures. */
    function renderFooter() {
      var p = pricing();
      renderSheetFooter(p);
      renderBar(p);
      refreshTriggerLabel();
      syncLegacyQty();
    }

    /* The bulk-savings card, the desktop headline price and the legacy ATC all
       read `#quantity`. The sheet owns quantity on mobile, so mirror it across
       or those surfaces silently disagree with the footer. */
    function syncLegacyQty() {
      var input = document.getElementById('quantity');
      if (input && String(state.qty) !== input.value) {
        input.value = String(state.qty);
      }
    }

    function renderCta(p) {
      if (state.busy) {
        F.cta.textContent = 'Adding to basket…';
        F.cta.classList.remove('is-prompt');
        F.cta.classList.add('is-adding');
        F.cta.disabled = true;
        return;
      }
      F.cta.classList.remove('is-adding');
      F.cta.disabled = false;
      F.cta.classList.toggle('is-prompt', !p.ready);
      if (p.ready) {
        F.cta.textContent = 'Add to Basket — ' + money(p.totalEx);
        return;
      }
      /* A blocked cut entry is the reason there is no price, so it owns the
         prompt — naming a missing option instead would point the shopper at
         a step they have already completed. */
      if (p.cut && p.cut.entered && !p.cut.fit) {
        F.cta.textContent = (p.cut.w === null || p.cut.l === null)
          ? 'Enter a width and a length'
          : 'Check your measurements';
        return;
      }

      var size = selectedSize();
      if (size && size.isCustom) {
        F.cta.textContent = state.customLen === ''
          ? 'Enter a length to continue'
          : 'Check your measurements';
        return;
      }
      /* Name what is actually missing — "Choose a thickness" on rubber,
         "Choose a size" on a mat — rather than a generic prompt. */
      var missingIndex = -1;
      state.selected.forEach(function (v, i) {
        if (v === null && missingIndex === -1) missingIndex = i;
      });
      var axisName = missingIndex >= 0 ? axes[missingIndex].name : 'an option';
      F.cta.textContent = 'Choose a ' + axisName.toLowerCase();
    }

    /* ================================================================ *
     *  Gallery sync
     *  ------------
     *  The sheet is standalone: it never clicks the legacy `.select_ul`
     *  option rows, and `custom-pdp-v2.js`'s variant → gallery swap is
     *  bound to exactly those clicks. So on mobile the photo behind the
     *  sheet stayed on whatever the page loaded with, and the shopper only
     *  discovered which colour they had actually chosen after closing the
     *  sheet — the point at which they are deciding whether to buy.
     *
     *  Slides are matched by MEDIA ID, never by position: the main slider
     *  can carry an extra leading slide (the tile-kit layout plan) and
     *  slick appends cloned slides, so `featured_media.position - 1` is
     *  not a slide index. The id is read back off `data-slick-index`.
     * ================================================================ */

    /* Slick initialises from eq-product-script.js on `$(document).ready`,
       which can land after this listener. A selection cannot happen before
       the shopper has opened the sheet, so this is defensive only — but a
       dropped goTo would leave the gallery silently wrong. */
    var pendingMediaId = null;

    function showMedia(mediaId) {
      var $ = window.jQuery;
      var nav = $ && $('.product-nav-slider');
      if (!nav || !nav.length || !nav.hasClass('slick-initialized')) {
        pendingMediaId = mediaId;
        return;
      }
      pendingMediaId = null;

      var slide = document.querySelector(
        '.product-nav-slider .main-product__slider-media[data-media-id="' +
        mediaId + '"]:not(.slick-cloned)'
      );
      if (!slide) return;
      var index = parseInt(slide.getAttribute('data-slick-index'), 10);
      if (isNaN(index)) return;

      /* Don't re-issue a goTo for the slide already showing — slick still
         runs its 300ms transition, which reads as a flicker when a shopper
         taps through sizes that all share one photo. */
      try {
        if (nav.slick('slickCurrentSlide') === index) return;
        nav.slick('slickGoTo', index);
      } catch (e) { /* slick mid-teardown */ }
    }

    window.addEventListener('load', function () {
      if (pendingMediaId !== null) showMedia(pendingMediaId);
    });

    /* The media that represents the selection SO FAR. Deliberately partial:
       picking a colour must swap the photo immediately, not wait for a size
       to be chosen too. Where several variants still match, the first one
       carrying a photo wins — the same "first variant with this option
       value" rule the data island uses to pick a colour's swatch, so the
       swatch the shopper tapped and the photo they get are the same image. */
    function representativeMediaId() {
      for (var i = 0; i < DATA.variants.length; i++) {
        var v = DATA.variants[i];
        var matches = true;
        for (var j = 0; j < axes.length; j++) {
          if (state.selected[j] !== null && v.options[j] !== state.selected[j]) {
            matches = false;
            break;
          }
        }
        if (matches && v.mediaId) return v.mediaId;
      }
      return null;
    }

    /* Not called at init — the gallery already opens on the product's own
       featured image, and moving it during first paint would fight the LCP
       fetch on the slide it is about to leave. */
    function syncGallery() {
      if (!isMobile()) return;
      var mediaId = representativeMediaId();
      if (mediaId) showMedia(mediaId);
    }

    /* ================================================================ *
     *  Interaction
     * ================================================================ */
    function pickAxis(axisIndex, value) {
      var prev = state.selected[axisIndex];
      var prevObj = prev !== null ? axisValueObj(axisIndex, prev) : null;
      state.selected[axisIndex] = value;
      state.banner = null;

      /* Switching between custom widths keeps the typed length; switching
         axis away from custom, or between custom and standard, leaves the
         length alone too — nothing is ever cleared silently. The only reset
         is a dependent re-clamp (below). */
      if (axisIndex === sizeAxisIndex) {
        var nowCustom = axisValueObj(axisIndex, value) && axisValueObj(axisIndex, value).isCustom;
        if (nowCustom && prev !== value) state.touched = state.customLen !== '';

        /* Picking a size by hand abandons a cut entry — the same rule the
           desktop panel applies. It is not only tidiness: a live entry
           RESOLVES the size axis (see `selectionValues`), so leaving it in
           place would make the tapped row appear to do nothing. */
        if (state.cutW !== '' || state.cutL !== '') {
          state.cutW = '';
          state.cutL = '';
        }
      }

      /* Dependent re-clamp. Changing thickness can shorten the maximum
         cuttable length (10 m roll → 5 m roll), which can leave a
         previously-valid typed length over the new ceiling. The handoff is
         explicit that this must be visible rather than silent: clear the
         value and state the new range, so the shopper is never left with a
         number the product cannot honour — and never sees a stale price for
         a length they can no longer order. */
      if (axisIndex !== sizeAxisIndex && state.customLen !== '') {
        var typed = parseLen();
        if (typed !== null && typed > maxCustomCm()) {
          state.customLen = '';
          state.touched = false;
          state.banner = 'That length is longer than the ' + maxLabel() +
                         ' roll this thickness comes on — enter a new length.';
        }
      }

      /* A change on a non-size axis must never clear the size. Where the new
         combination has no variant (a colour that does not come in the
         selected size), the size is kept and the CTA falls back to its
         prompt state rather than silently choosing a different size. */
      renderBody();
      renderFooter();
      syncGallery();

      /* Opening the custom group used to leave its length input below the
         fold — the group sits at the bottom of the size step, and nothing
         told the shopper the field existed. Scroll it fully into view the
         moment custom becomes the selection (and only then — re-renders
         from the width segments must not keep yanking the scroll). */
      if (axisIndex === sizeAxisIndex && body) {
        var nowObj = axisValueObj(axisIndex, value);
        var wasCustom = !!(prevObj && prevObj.isCustom);
        if (nowObj && nowObj.isCustom && !wasCustom) {
          var grp = body.querySelector('.fm-sheet__custom');
          if (grp && grp.scrollIntoView) {
            grp.scrollIntoView({ block: 'end', behavior: 'smooth' });
          }
        }
      }
    }

    on(F.inc, 'click', function () {
      state.qty += 1;
      renderFooter();
    });
    on(F.dec, 'click', function () {
      state.qty = Math.max(1, state.qty - 1);
      renderFooter();
    });

    /* ---- page footer controls ---- */
    bar.querySelectorAll('[data-fm-buybar-open]').forEach(function (b) {
      b.addEventListener('click', function (e) {
        e.preventDefault();
        openSheet();
      });
    });
    on(B.inc, 'click', function () {
      state.qty += 1;
      renderFooter();
    });
    on(B.dec, 'click', function () {
      state.qty = Math.max(1, state.qty - 1);
      renderFooter();
    });
    on(B.cta, 'click', function () {
      var p = pricing();
      /* The footer CTA only ever appears priced and ready — an incomplete
         selection shows the slim trigger bar instead, so there is no prompt
         branch to handle here. The guard is for an out-of-stock single SKU. */
      if (p.ready) addToCart(p, B.cta);
    });

    /* ---- CTA ---- */
    on(F.cta, 'click', function () {
      var p = pricing();
      if (!p.ready) {
        /* Prompt state: say what is needed and put the cursor where it is
           needed. This is what makes the grey CTA a live control rather
           than a dead tap. */
        if (p.cut && p.cut.entered && !p.cut.fit) {
          state.banner = p.cut.error;
          renderFooter();
          updateCutPanel();
          focusCutField(state.cutW === '' ? 'w' : 'l');
          return;
        }
        var size = selectedSize();
        if (size && size.isCustom) {
          state.banner = 'Enter a length of ' + MIN_CUSTOM_CM + ' cm or more to see your price.';
          state.touched = true;
          renderFooter();
          updateHelper();
          var input = sq('[data-fm-sheet-len]');
          if (input) input.focus();
          return;
        }
        var missingIndex = -1;
        state.selected.forEach(function (v, i) {
          if (v === null && missingIndex === -1) missingIndex = i;
        });
        var name = missingIndex >= 0 ? axes[missingIndex].name.toLowerCase() : 'an option';
        state.banner = 'Choose a ' + name + ' above — your price will appear here.';
        renderFooter();
        /* body children follow renderOrder(), not axis order. */
        var displayPos = renderOrder().indexOf(missingIndex >= 0 ? missingIndex : 0);
        var step = body.children[displayPos >= 0 ? displayPos : 0];
        if (step && step.scrollIntoView) {
          step.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
        return;
      }
      addToCart(p, F.cta);
    });

    /* ---------------------------------------------------------------- *
     *  Add to cart — delegates to the legacy globals
     * ---------------------------------------------------------------- */
    /* The selection is now in the basket, so the decision it represented is
       finished — put the sheet and the footer back to their fresh state
       while the cart drawer is still on top. When the shopper closes the
       drawer they land on the slim "Select colour & size" bar, not on a
       selected summary whose CTA would re-add what they just bought.
       (Replaces the earlier "Add another — £X" post-add CTA — Jack found a
       primed repeat-add button too easy to hit by accident, 31-Jul-2026.)
       Visual preselects (colour, texture) come back; quantity, custom
       length and every blocking axis clear. */
    function resetSelection() {
      applyPreselects();
      state.qty = 1;
      state.customLen = '';
      state.touched = false;
      state.cutW = '';
      state.cutL = '';
      state.cutOpen = false;
      state.banner = null;
      state.busy = false;
      renderBody();
      renderFooter();
      /* The reset puts the colour swatch back to the preselected value, so
         the gallery has to come back with it — otherwise reopening the sheet
         after an add shows one colour selected and a different one in the
         photo, which is the mismatch this sync exists to remove. */
      syncGallery();
    }

    /* Poll for the cart drawer's `.active` — the one signal every add path
       shares, including the legacy fire-and-forget helpers that offer no
       callback. 10s cap so a dropped request can't strand the button. */
    function whenDrawerOpens(onOpen, onTimeout) {
      var waited = 0;
      var timer = setInterval(function () {
        var drawer = document.querySelector('cart-drawer.active, #CartDrawer.active');
        if (drawer) {
          clearInterval(timer);
          onOpen();
          return;
        }
        waited += 150;
        if (waited >= 10000) {
          clearInterval(timer);
          onTimeout();
        }
      }, 150);
    }

    /* `btn` is whichever CTA was pressed — the sheet's or the page footer's.
       Both render the busy state from `state.busy`, so whichever is on
       screen shows "Adding to basket…" until the drawer takes over. */
    function addToCart(p, btn) {
      var variant = p.variant;
      if (!variant) return;
      if (state.busy) return; /* in flight — ignore the spam tap */

      /* Line-item properties are what the cart drawer renders, so every
         axis has to appear here — an option omitted from properties is an
         option the shopper cannot see in their basket. */
      var properties = {};
      var partNo = partNumberFor(variant);
      if (partNo) properties['Part No.'] = partNo;
      /* `selectionValues`, not `state.selected` — on a cut-to-size line the
         size property must be the sheet being bought, which is what the
         variant id already is. */
      var values = selectionValues();
      axes.forEach(function (axis, i) {
        var v = values[i];
        if (!v) return;
        /* Shopify gives an option-less product a synthetic
           "Title: Default Title" option. Posting it would show the shopper a
           meaningless "Title — Default Title" row in their basket. */
        if (axis.name === 'Title' && v === 'Default Title') return;
        properties[axis.name] = v;
      });

      /* Cut dimensions. Same three keys, same 'mm' suffix and the same
         `Cut from` sheet label as the desktop ATC handler in
         `nocustom-multioptions-form-v2.liquid` — fulfilment reads these, so
         the two surfaces must post identical property names. */
      if (p.isCut) {
        properties['Custom width'] = p.cut.w + 'mm';
        properties['Custom length'] = p.cut.l + 'mm';
        /* The FULL option value ("600mm x 800mm (2' x 2.5')"), not the display
           label the sheet shows — the desktop panel records the full value and
           these two must read identically on a picking list. */
        properties['Cut from'] = p.cut.fit.value;
      }

      state.busy = true;
      renderFooter();

      /* The add landed: the drawer is up, so the sheet closes behind it and
         everything resets to the fresh state (slim bar, qty 1) — closing the
         drawer never lands on a primed repeat-add CTA (Jack, 31-Jul-2026). */
      function settle() {
        whenDrawerOpens(function () {
          state.busy = false;
          closeSheet();
          resetSelection();
        }, function () {
          /* No drawer after 10s — assume the add failed. Keep the selection
             so the shopper can simply try again. */
          state.busy = false;
          state.banner = 'Sorry — that could not be added. Please try again.';
          renderFooter();
        });
      }

      if (p.isCustom) {
        properties['Length (cm)'] = String(parseLen());
        if (typeof window.customLengthProductAdded === 'function') {
          window.customLengthProductAdded(variant.id, parseLen(), properties, state.qty);
          settle();
          return;
        }
      } else if (typeof window.customProductAdded === 'function') {
        window.customProductAdded(variant.id, state.qty, properties);
        settle();
        return;
      }

      /* Fallback — the legacy helpers live in an inline script that only the
         customisable product templates render. Post directly so the sheet
         still works if it is ever mounted on a template without them. */
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variant.id,
          quantity: p.isCustom && !DATA.percmUnitQty ? parseLen() : state.qty,
          properties: properties
        })
      }).then(function () {
        if (typeof window.addToCartAndOpenDrawer === 'function') {
          window.addToCartAndOpenDrawer();
          settle();
        } else {
          window.location.href = '/cart';
        }
      }).catch(function () {
        state.busy = false;
        state.banner = 'Sorry — that could not be added. Please try again.';
        renderFooter();
      });
    }

    /* ================================================================ *
     *  Open / close
     * ================================================================ */
    var lastFocus = null;
    var savedScrollY = 0;

    function isOpen() {
      return !!root && root.getAttribute('data-state') === 'open';
    }

    function openSheet() {
      if (!root) return;
      if (!isMobile() || isOpen()) return;
      lastFocus = document.activeElement;
      savedScrollY = window.scrollY || window.pageYOffset || 0;

      root.removeAttribute('inert');
      root.setAttribute('aria-hidden', 'false');
      root.setAttribute('data-state', 'open');
      document.body.classList.add('fm-sheet-open');
      document.body.style.top = '-' + savedScrollY + 'px';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';

      var closeBtn = sq('.fm-sheet__close');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
      document.addEventListener('keydown', onKeydown);
    }

    /* `reveal` is set by the closes a shopper performs to go and LOOK at
       something — drag, X, backdrop, Escape. The post-add close and the
       leaving-mobile close pass nothing: the cart drawer is coming up over
       the page in one case, and the sheet is being torn down in the other,
       so a scroll would be noise either way. */
    function closeSheet(reveal) {
      if (!root) return;
      root.setAttribute('data-state', 'closed');
      root.setAttribute('aria-hidden', 'true');
      root.setAttribute('inert', '');
      document.body.classList.remove('fm-sheet-open');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, savedScrollY);
      document.removeEventListener('keydown', onKeydown);

      if (F.cta) {
        F.cta.classList.remove('is-adding');
        F.cta.disabled = false;
      }

      if (lastFocus && typeof lastFocus.focus === 'function') {
        lastFocus.focus({ preventScroll: true });
      }

      if (reveal) revealGallery();
    }

    /* ================================================================ *
     *  Drag to close
     *  -------------
     *  The header has always carried a grabber pill, which is the standard
     *  "you can drag this" affordance — but nothing was bound to it, so the
     *  sheet read as broken to anyone who tried. Shoppers reached for the
     *  gesture specifically to get a look at the photo behind the sheet,
     *  and had to hunt for the X instead.
     *
     *  Closing is the right end state rather than a half-open detent: the
     *  buybar underneath already carries the chosen chips and the priced
     *  CTA, so nothing is lost on the way down and "Change" brings the
     *  sheet straight back.
     * ================================================================ */

    /* Two ways to commit to closing: pull past this much of the panel's own
       height, or release while still moving faster than this. 0.4 / 0.6
       px-per-ms is the usual platform-sheet feel — a deliberate half-pull
       closes, a small nudge does not.

       The flick path additionally needs FLICK_MIN_PX of travel. Velocity on
       its own is measured across the last couple of touchmove events, so a
       twitch of a few pixels can read as 2 px/ms and close a sheet the
       shopper was only brushing past. */
    var DRAG_CLOSE_RATIO = 0.4;
    var DRAG_CLOSE_VELOCITY = 0.6;
    var FLICK_MIN_PX = 56;

    var drag = null;

    function dragPoint(e) {
      return e.touches && e.touches.length ? e.touches[0] : e;
    }

    function onDragStart(e) {
      var panel = sq('.fm-sheet__panel');
      var header = sq('.fm-sheet__header');
      if (!panel || !isOpen() || drag) return;

      var fromHeader = !!(header && header.contains(e.target));
      /* A drag may also start in the scrollable body, but only when it is
         already scrolled to the top — otherwise the gesture belongs to the
         option list. Those starts are provisional (`active: false`) until
         the movement proves to be a downward drag rather than a scroll or a
         sideways swipe across the swatch row. */
      var fromBodyTop = !!(body && body.contains(e.target) && body.scrollTop <= 0);
      if (!fromHeader && !fromBodyTop) return;

      var p = dragPoint(e);
      drag = {
        x0: p.clientX,
        y0: p.clientY,
        dy: 0,
        active: fromHeader,
        lastY: p.clientY,
        lastT: Date.now(),
        vy: 0,
        panel: panel
      };
    }

    function onDragMove(e) {
      if (!drag) return;
      var p = dragPoint(e);
      var dy = p.clientY - drag.y0;
      var dx = p.clientX - drag.x0;

      if (!drag.active) {
        /* Sideways wins — the colour row scrolls horizontally. So does any
           upward movement, which is the body scrolling back down. */
        if (Math.abs(dx) > Math.abs(dy) || dy < 0) { drag = null; return; }
        if (dy < 8) return;
        drag.active = true;
      }

      if (dy < 0) dy = 0;              /* the sheet does not drag up past open */
      var now = Date.now();
      var dt = now - drag.lastT;
      if (dt > 0) drag.vy = (p.clientY - drag.lastY) / dt;
      drag.lastY = p.clientY;
      drag.lastT = now;
      drag.dy = dy;

      drag.panel.style.transition = 'none';
      drag.panel.style.transform = 'translateY(' + dy + 'px)';

      /* Fade the backdrop with the drag so the page behind is already
         readable on the way down — the whole point of the gesture. */
      var backdrop = sq('.fm-sheet__backdrop');
      if (backdrop) {
        var h = drag.panel.offsetHeight || 1;
        backdrop.style.opacity = String(Math.max(0, 1 - (dy / h)));
      }

      /* Non-passive listener — without this the page scrolls underneath. */
      if (e.cancelable) e.preventDefault();
    }

    function onDragEnd() {
      if (!drag) return;
      var panel = drag.panel;
      var height = panel.offsetHeight || 1;
      var shouldClose = drag.active && (
        drag.dy > height * DRAG_CLOSE_RATIO ||
        (drag.dy > FLICK_MIN_PX && drag.vy > DRAG_CLOSE_VELOCITY)
      );
      drag = null;

      var backdrop = sq('.fm-sheet__backdrop');
      panel.style.transition = '';
      if (backdrop) backdrop.style.opacity = '';
      /* Force a recalc while the inline transform is still applied, so the
         snap-back / close animates FROM where the finger left it. Clearing
         both in one frame lands the panel at its destination instantly. */
      void panel.offsetHeight;
      panel.style.transform = '';

      if (shouldClose) closeSheet(true);
    }

    function onDragCancel() {
      if (!drag) return;
      var panel = drag.panel;
      drag = null;
      var backdrop = sq('.fm-sheet__backdrop');
      panel.style.transition = '';
      if (backdrop) backdrop.style.opacity = '';
      void panel.offsetHeight;
      panel.style.transform = '';
    }

    (function bindDrag() {
      var panel = sq('.fm-sheet__panel');
      if (!panel) return;
      panel.addEventListener('touchstart', onDragStart, { passive: true });
      panel.addEventListener('touchmove', onDragMove, { passive: false });
      panel.addEventListener('touchend', onDragEnd);
      panel.addEventListener('touchcancel', onDragCancel);
    })();

    /* Bring the gallery back into view on the way out. A shopper who opened
       the sheet from further down the page (specs, description) would
       otherwise slide it away to reveal that content rather than the photo
       they were trying to check. Skipped when the gallery is already showing
       — most opens happen near the top of the page, and a jump nobody asked
       for is worse than no jump at all. */
    function revealGallery() {
      var gallery = document.querySelector('.lm-pdp-gallery .main-product__slider-main') ||
                    document.querySelector('.lm-pdp-gallery');
      if (!gallery || !gallery.getBoundingClientRect) return;

      var rect = gallery.getBoundingClientRect();
      var viewport = window.innerHeight || document.documentElement.clientHeight || 0;
      if (!viewport || !rect.height) return;

      var visible = Math.min(rect.bottom, viewport) - Math.max(rect.top, 0);
      if (visible >= Math.min(rect.height, viewport) * 0.5) return;

      /* `center` rather than `start` — the theme's sticky header would cover
         the top of the image, and the gallery is shorter than the viewport
         on every phone size we support. */
      if (gallery.scrollIntoView) {
        gallery.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    function onKeydown(e) {
      if (e.key === 'Escape' || e.keyCode === 27) {
        e.preventDefault();
        closeSheet(true);
        return;
      }
      if (e.key === 'Tab' || e.keyCode === 9) trapFocus(e);
    }

    function trapFocus(e) {
      var panel = sq('.fm-sheet__panel');
      if (!panel) return;
      var focusables = panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]),' +
        ' select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    triggers.forEach(function (t) {
      t.addEventListener('click', function (e) {
        e.preventDefault();
        openSheet();
      });
    });
    if (root) {
      root.querySelectorAll('[data-fm-sheet-close]').forEach(function (n) {
        n.addEventListener('click', function () { closeSheet(true); });
      });
    }

    /* Leaving the mobile breakpoint with the sheet open (rotation, tablet)
       hides the panel via the media query but would leave the body scroll-
       locked with no way back. Close it properly instead. */
    window.addEventListener('resize', function () {
      if (!isMobile() && isOpen()) closeSheet();
    });

    /* The sticky page ATC must not add a variant the sheet has not resolved.
       Intercept in the capture phase — the legacy form binds its own handler
       to the same element, so `stopImmediatePropagation` (not just
       `stopPropagation`) is what blocks it. */
    var pageAtc = document.querySelector('.custom-atc-basket');
    if (pageAtc) {
      pageAtc.addEventListener('click', function (e) {
        if (!isMobile()) return;
        if (isOpen()) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        openSheet();
      }, true);
    }

    /* ---- inline "Choose your options" CTA ---- */
    /* In-flow prompt between the need-it-fast countdown and the bulk-pricing
       card. Opens the same sheet, and while it is on screen the fixed buy
       bar hides (body.fm-inline-cta-onscreen, consumed by this component's
       CSS and by fm-consent-banner.js) — never two calls to action at once.
       The counts line is derived from the sheet's own axes, not re-counted
       from Liquid, so it can never disagree with what the sheet offers. */
    var inlineCta = document.querySelector('[data-fm-inline-cta]');
    if (inlineCta) {
      inlineCta.addEventListener('click', function (e) {
        e.preventDefault();
        openSheet();
      });

      var inlineCounts = inlineCta.querySelector('[data-fm-inline-counts]');
      if (inlineCounts && axes.length) {
        inlineCounts.textContent = axes.map(function (a) {
          var n = a.values.length;
          var word = a.name.toLowerCase();
          /* "thickness" → "thicknesses", not "thicknesss" */
          if (n !== 1) word += /(s|x|ch|sh)$/.test(word) ? 'es' : 's';
          return n + ' ' + word;
        }).join(' · ') + ' · ';
      }

      /* Half on screen counts as "on screen" — enough that a shopper is
         clearly looking at it before the bar stands down. Without
         IntersectionObserver the bar simply always stays, which is safe. */
      if (window.IntersectionObserver) {
        new IntersectionObserver(function (entries) {
          var showing = entries[0].isIntersecting && isMobile();
          document.body.classList.toggle('fm-inline-cta-onscreen', showing);
        }, { threshold: 0.5 }).observe(inlineCta);
      }
    }

    /* Keep the closed-state trigger label showing the current selection, so
       the shopper knows what they picked without reopening the sheet. */
    function refreshTriggerLabel() {
      var parts = [];
      axes.forEach(function (axis, i) {
        if (i === sizeAxisIndex) return;
        if (state.selected[i]) parts.push(state.selected[i]);
      });
      var sel = selectionLabel();
      if (sel) parts.push(sel);
      var text = parts.length ? parts.join(' · ') : 'Select ' +
        axes.map(function (a) { return a.name; }).join(' & ');
      triggers.forEach(function (t) {
        var label = t.querySelector('.pdp-options-trigger__label');
        if (label) label.textContent = text;
      });
    }

    /* The footer's height changes with its mode (slim is much shorter than the
       selected summary), so the page's bottom padding has to be measured
       rather than guessed — otherwise the last of the page content sits under
       it after the shopper picks a size. */
    function updateBarHeight() {
      if (!isMobile()) return;
      var h = bar.getBoundingClientRect().height;
      if (h > 0) {
        document.documentElement.style.setProperty('--fm-buybar-height', h + 'px');
      }
    }
    if (window.ResizeObserver) {
      try {
        new ResizeObserver(updateBarHeight).observe(bar);
      } catch (e) { /* falls back to the resize listener below */ }
    }
    window.addEventListener('resize', updateBarHeight);

    /* ---- init ---- */
    if (isMobile()) {
      document.body.classList.add('fm-sheet-active');
    }
    window.addEventListener('resize', function () {
      document.body.classList.toggle('fm-sheet-active', isMobile());
    });

    /* fm-vat.js flips the rest of the page (incl_vat=true landings, gclid,
       the manual toggle). The sheet renders its prices from data, not from
       [data-ex-price] attributes, so it re-renders itself on the same
       event — one source of truth for the mode, no drift between the page
       and the sheet. */
    document.addEventListener('fm:vat-changed', function () {
      renderBody();
      renderFooter();
    });

    renderBody();
    renderFooter();
    updateBarHeight();
  });
})();
